import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDisasterType, setSelectedDisasterType] = useState('all');
  const [selectedCommodity, setSelectedCommodity] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    avgLossPercentage: 0,
    totalValueLoss: 0,
    totalClaims: 0
  });
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapView, setMapView] = useState('before'); // 'before' or 'current'

  const fetchEvents = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.disasterType && filters.disasterType !== 'all') {
        params.append('disasterType', filters.disasterType);
      }
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/events?${params.toString()}`);
      setEvents(response.data.events);
      setStats(response.data.stats);

      // Set first event as selected if none selected
      if (response.data.events.length > 0 && !selectedEvent) {
        setSelectedEvent(response.data.events[0]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      // Set empty data on error to prevent crashes
      setEvents([]);
      setStats({
        totalEvents: 0,
        avgLossPercentage: 0,
        totalValueLoss: 0,
        totalClaims: 0
      });
    } finally {
      setLoading(false);
    }
  }, [selectedEvent]);

  const fetchEventDetails = useCallback(async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setSelectedEvent(response.data.event);
      setTimeline(response.data.timeline);
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  }, []);

  const fetchCommodities = useCallback(async () => {
    try {
      const response = await api.get('/commodities');
      setCommodities(response.data.commodities);
      if (response.data.commodities.length > 0 && !selectedCommodity) {
        setSelectedCommodity(response.data.commodities[0]);
      }
    } catch (error) {
      console.error('Error fetching commodities:', error);
      // Set empty array on error
      setCommodities([]);
    }
  }, [selectedCommodity]);

  const submitAdjustment = useCallback(async (eventId, adjustmentData) => {
    try {
      const response = await api.put(`/events/${eventId}/adjustment`, adjustmentData);
      setSelectedEvent(response.data.event);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit adjustment'
      };
    }
  }, []);

  const initiateClaim = useCallback(async (eventId) => {
    try {
      const response = await api.put(`/events/${eventId}/claim`);
      setSelectedEvent(response.data.event);
      return { success: true, claimNumber: response.data.claimNumber };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to initiate claim'
      };
    }
  }, []);

  const generateReport = useCallback(async (eventId) => {
    try {
      const response = await api.post(`/reports/generate/${eventId}`, {}, {
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON) disguised as blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        return {
          success: false,
          message: error.message || 'Failed to generate report'
        };
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Geoventory_Report_${selectedEvent?.erfNumber || eventId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      // Handle blob error response
      let errorMessage = 'Failed to generate report';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const jsonError = JSON.parse(text);
          errorMessage = jsonError.message || errorMessage;
        } catch (e) {
          // If we can't parse the blob, use default message
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return {
        success: false,
        message: errorMessage
      };
    }
  }, [selectedEvent]);

  const createEvent = useCallback(async (eventData) => {
    try {
      const response = await api.post('/events', eventData);
      setEvents(prev => [response.data.event, ...prev]);
      return { success: true, event: response.data.event };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create event'
      };
    }
  }, []);

  const value = {
    events,
    selectedEvent,
    setSelectedEvent,
    selectedDisasterType,
    setSelectedDisasterType,
    selectedCommodity,
    setSelectedCommodity,
    commodities,
    stats,
    timeline,
    loading,
    mapView,
    setMapView,
    fetchEvents,
    fetchEventDetails,
    fetchCommodities,
    submitAdjustment,
    initiateClaim,
    generateReport,
    createEvent
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
